import { StreetOrientation } from "building-types/building/streetOrientationTypesEnum";
import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { DocEntityHandle, DormerHandle } from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { RemoveFormula } from "building-types/options/formula";
import { DEFAULT_DORMER_CORNER_TRIM_PROFILE_DESCRIPTOR } from "building-types/profiles/defaultBoardTrimProfiles";
import type { ProfileId } from "building-types/profiles/profileTypes";
import { ProfileExtrusionType } from "building-types/profiles/profileTypesEnum";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import type { RoofEdge } from "building-types/roofs/roofEdgeTypes";
import type {
  DormerParentRoofSettings,
  GenerateRoofOptions,
  RoofDormer,
  RoofDormerWallBaseBoardRails,
  RoofDormerWallBaseBoardSection,
} from "building-types/roofs/roofGenerationTypes";
import type {
  DormerInputSettings,
  GetMergedSettingsForDormer,
  RoofInputSettings,
  RoofVolumeInputSettings,
} from "building-types/roofs/roofInputSettings";
import type { RoofSlope } from "building-types/roofs/roofSlope";
import type {
  EvaluatedRoofData,
  RoofOpeningPlacement,
  RoofDormerPlacement,
} from "building-types/roofs/roofSystemTypes";
import type { GenerateRoofVolumeOptions } from "building-types/roofVolumes/roofVolumeGenerationTypes";
import type { GeneratedRoofVolumeLegacy } from "building-types/roofVolumes/roofVolumeTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import type { DormerSettings } from "building-types/settings/dormerSettings";
import type { ExteriorTrimSettings } from "building-types/settings/exteriorTrimSettings";
import type { MergeSettingsForEntityParams } from "building-types/settings/mergeSettingsForEntityParams";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import { RoofTruncationType } from "building-types/settings/roofBaseTypes";
import type { RoofSettings } from "building-types/settings/roofSettings";
import type { EvaluatedRoofEdgeOverrides, EvaluatedRoofSideOverridesTable } from "building-types/settings/roofTypes";
import { BuildingSide, defaultRoofEdgeOverrides, DormerRoofType, RoofSide } from "building-types/settings/roofTypes";
import {
  CornerBoardTrimAlignment,
  DormerWallBaseBoardMode,
  GableRoofType,
  RoofPeakDirection,
} from "building-types/settings/settingsTypesEnum";
import { ExteriorTrimViewNoteType } from "building-types/settings/viewNotesSettings";
import type { WallSettings } from "building-types/settings/wallSettings";
import { ValidCornerCondition, type CornerBoardRail } from "building-types/trim/cornerBoardTypes";
import type { FaceArrayViewEntity } from "building-types/views/viewEntity";
import { ViewEntityType } from "building-types/views/viewEntityType";
import { BuiltinWindowId } from "building-types/windows/windowId";
import type {
  GeneratedWindowPlacement,
  WindowDescriptor,
  WindowShutters,
  WindowUnit,
} from "building-types/windows/windowTypes";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import uid from "common/utils/uid";
import { WINDOW_EXTERIOR_FRAME_WIDTH } from "doc/elements/window/constants";
import { defaultWindowCatalog } from "doc/elements/window/windowCatalog";
import { defaultAllExteriorTrimViewNotes } from "doc/settings/defaultViewNoteSettings";
import { BuildingGenerationContext } from "doc/utils/buildingGenerationContext";
import { MULTIGABLE_BSP_EPSILON, neighborRoofNew, neighborRoofsNew, roofInputPerimetersNew } from "doc/utils/roofTools";
import { viewEntityDescriptorNew } from "doc/utils/viewEntityTools";
import { mergeViewNoteTables } from "doc/utils/viewNotesSettingsTools";
import { getExteriorWallOffset } from "doc/utils/wallTypeTools";
import { aabb2Width } from "geom/aabb2Types";
import { aabb2Height } from "geom/aabb2Types";
import type { Face } from "geom/brep";
import { brepNew, faceOrNullCopy, faceOrNullMoveAlongNormal } from "geom/brep";
import { clippingNodeNew, clipRail } from "geom/bspClipping";
import { DIST_EPSILON } from "geom/core";
import { extrudePolyLine2, extrusionToBRep } from "geom/extrude";
import { holeWithBackFaceIndex } from "geom/hole";
import { offsetPolyLine2 } from "geom/offset";
import {
  planeEval,
  planeEvalDir,
  planeNew,
  planeNewXY,
  planeNormal,
  planeProject,
  planeReProjectPolyLine2,
  polyLine2OnPlane,
} from "geom/plane";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2AABB2,
  polyLine2ByTwoPoints,
  polyLine2Close,
  polyLine2Concat,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2EdgeLength,
  polyLine2EdgeStart,
  polyLine2EdgeTangent,
  polyLine2Move,
  polyLine2Reverse,
  polyLine2Simplify,
} from "geom/polyLine2";
import { LineAnchorEnum, polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3EdgeStart, polyLine3EdgeTangent, polyLine3OnPlane } from "geom/polyLine3";
import { ray2Project } from "geom/ray2";
import type { Vec2 } from "geom/vec2";
import { vec2Dist, vec2Dot, vec2Normalize } from "geom/vec2";
import { vec3AngleBetween, vec3CrossNormalize, vec3Dot, vec3Normalize } from "geom/vec3";
import { makeOpeningSide } from "../../elements/openings/openingProfile";
import { windowOpeningNew } from "../../elements/openings/windowOpenings";
import { pushMissingProfileReferenceIssue } from "../../elements/profileMissingReference";
import { getActiveProfileDescriptor, getProfileExtrusionDescriptorProfile } from "../../elements/profileTools";
import { getEaveDepth } from "../../elements/roofTrimUtility";
import { makeWindowShutters } from "../../elements/shutters";
import { windowRectangularOpening } from "../../elements/window/windowElevation";
import { getWindowHeight } from "../../elements/window/windowHeightTools";
import { windowHasCasing } from "../../elements/window/windowQuery";
import { generatedWindowPlacementDeprecatedNew } from "../../elements/window/windowTools";
import { makeWindowUnit } from "../../elements/window/windowUnit";
import type { WindowShuttersPlacement } from "../../elements/window/windowUtils";
import {
  makeDefaultWindowShuttersPlacement,
  makeDefaultWindowShuttersPlacementLegacy,
} from "../../elements/window/windowUtils";
import { buildCornerBoardProfile } from "../cornerBoards/cornerBoardTools";
import { getFinalDormerFramingSettings, getFinalDormerRoofSettings, getFinalRoofPlanSettings } from "../roofs";
import { generateMultiGableRoof } from "../roofs/generate/generateRoof";
import { roofDormerWallBaseBoardSectionNew } from "../roofs/generate/roofGeneration";
import { getMergedDormerSettings } from "../roofs/getMergedRoofSettings";
import { getRoofEdgeFromSide } from "../roofs/roofEdges";
import { getSubRoofSlopes } from "../roofs/roofSlopes";
import { generateRoofVolumeLegacy } from "../roofVolumes/generateLegacy/generateRoofVolume";

const EXTRA_DORMER_WINDOW_BORDER = 8;
export const DORMER_WINDOW_DEFAULT_OFFSET = 12;

function roofDormerNew(
  pickHandle: DocEntityHandle,
  entityHandleForSelectingReportingUnit: DocEntityHandle | undefined,
  hostRoofSystemId: string,
  hostSubRoofId: string,
  subRoofId: string,
  levelId: string,
  frontEdge: PolyLine3,
  frontHeight: number,
  totalHeight: number,
  wall: Face[],
  hideInteriorFaces: Face[],
  windowHoleFaces: Face[],
  windows: Table<GeneratedWindowPlacement>,
  cornerBoards: FaceArrayViewEntity[],
  wallBaseBoardRails?: RoofDormerWallBaseBoardRails,
  wallMaterial?: ExteriorMaterial,
): RoofDormer {
  return {
    pickHandle,
    entityHandleForSelectingReportingUnit,
    hostRoofSystemId,
    hostSubRoofId,
    subRoofId,
    levelId,
    frontEdge,
    frontHeight,
    totalHeight,
    wall,
    hideInteriorFaces,
    windowHoleFaces,
    windows,
    cornerBoards,
    wallBaseBoards: [],
    wallBaseBoardRails,
    wallMaterial,
  };
}

function roofDormerWallBaseBoardRailsNew(
  sections: RoofDormerWallBaseBoardSection[],
  height: number,
  profileId: ProfileId,
): RoofDormerWallBaseBoardRails {
  return {
    sections,
    height,
    profileId,
  };
}

function faceSlopeDir2(face: Face): Vec2 {
  const [uu, uv] = planeProject(face.plane, face.plane.originX, face.plane.originY, face.plane.originZ + 1);

  let [slopex, slopey, slopez] = planeEvalDir(face.plane, uu, uv);
  [slopex, slopey, slopez] = vec3Normalize(slopex, slopey, slopez);

  return vec2Normalize(slopex, slopey);
}

export function roofFaceBuildingSide(face: Face, mirror: boolean = false): BuildingSide {
  const [slopex, slopey] = faceSlopeDir2(face);

  const xdot = vec2Dot(1, 0, slopex, slopey);

  if (Math.abs(xdot - 1) < DIST_EPSILON) {
    return mirror ? BuildingSide.RIGHT : BuildingSide.LEFT;
  } else if (Math.abs(xdot + 1) < DIST_EPSILON) {
    return mirror ? BuildingSide.LEFT : BuildingSide.RIGHT;
  }

  const ydot = vec2Dot(0, 1, slopex, slopey);

  if (Math.abs(ydot - 1) < DIST_EPSILON) {
    return BuildingSide.FRONT;
  } else if (Math.abs(ydot + 1) < DIST_EPSILON) {
    return BuildingSide.BACK;
  }

  return BuildingSide.DIAGONAL;
}

function getDormerWindowDescriptor(
  dormerOptions: RoofDormerPlacement,
  windowCatalog: Table<WindowDescriptor> | undefined,
): WindowDescriptor {
  let windowDescriptor: WindowDescriptor | undefined = undefined;
  if (dormerOptions.windowType !== undefined && windowCatalog) {
    windowDescriptor =
      windowCatalog[dormerOptions.windowType] ??
      defaultWindowCatalog[dormerOptions.windowType ?? BuiltinWindowId.G2030];
  }
  return windowDescriptor ?? defaultWindowCatalog[BuiltinWindowId.G2030];
}

function getDormerWindows(
  dormerOptions: RoofDormerPlacement,
  getMergedWindowSettingsForDormer: GetMergedSettingsForDormer,
  streetOrientation: StreetOrientation,
  windowCatalog?: Table<WindowDescriptor>,
): GeneratedWindowPlacement[] {
  const descriptor = getDormerWindowDescriptor(dormerOptions, windowCatalog);
  const { exteriorCasing } = getMergedWindowSettingsForDormer(dormerOptions, descriptor);
  const hasExteriorCasing = windowHasCasing({ descriptor, streetOrientation }, exteriorCasing);

  const window = generatedWindowPlacementDeprecatedNew({
    id: uid(),
    descriptor,
    frontRoom: undefined, // dormer windows don't have a front room
    halfEdgeIndex: 0,
    param: 0.5,
    paramNormalized: true,
    paramAnchor: LineAnchorEnum.START,
    headHeight: undefined,
    operationType: dormerOptions.operationType,
    materials: dormerOptions.materials,
    opening: undefined,
    verticalOffset: dormerOptions.windowVerticalOffset ?? DORMER_WINDOW_DEFAULT_OFFSET,
    verticalDivisionSpacing: dormerOptions.verticalDivisionSpacing,
    mullions: dormerOptions.mullions,
    exteriorCasing,
    shutters: dormerOptions.shutters,
    awning: dormerOptions.awning,
    awningSettings: dormerOptions.awningSettings,
    viewNotes: dormerOptions.viewNotes,
    disableAutoDimensions: dormerOptions.disableAutoDimensions,
    hasExteriorCasing,
    streetOrientation,
  });

  return [window];
}

function getDormerFrontOffset(dormerOptions: DormerSettings): number {
  return dormerOptions.frontOffset ?? 0;
}

function getDormerWidthFromWindow(windowDescriptor: WindowDescriptor) {
  const windowFrameWidth = WINDOW_EXTERIOR_FRAME_WIDTH + EXTRA_DORMER_WINDOW_BORDER;
  return windowDescriptor.width + windowFrameWidth * 2;
}

function getDormerHeightFromWindow(windowType: WindowDescriptor) {
  const windowFrameWidth = WINDOW_EXTERIOR_FRAME_WIDTH + EXTRA_DORMER_WINDOW_BORDER;
  const height = getWindowHeight(windowType);
  return height + windowFrameWidth + DORMER_WINDOW_DEFAULT_OFFSET;
}

export function getDormerWidth(
  dormerSettings: DormerSettings,
  dormerOptions: RoofDormerPlacement,
  windowCatalog: Table<WindowDescriptor>,
): number {
  const dormerMinFrontWidth = 54.0;

  if (dormerSettings.width !== undefined) {
    return dormerSettings.width;
  } else {
    const windowType = getDormerWindowDescriptor(dormerOptions, windowCatalog);
    return Math.max(getDormerWidthFromWindow(windowType), dormerMinFrontWidth);
  }
}

function getDormerHeight(
  dormerSettings: DormerSettings,
  dormerOptions: RoofDormerPlacement,
  slopeAngle: number,
  windowCatalog: Table<WindowDescriptor>,
): number {
  const dormerMinFrontHeight = 78.0;

  let height = 0.0;
  if (dormerSettings.height !== undefined) {
    height = dormerSettings.height;
  } else {
    const windowType = getDormerWindowDescriptor(dormerOptions, windowCatalog);
    height = Math.max(getDormerHeightFromWindow(windowType), dormerMinFrontHeight);
  }

  // add front offset
  let dormerDiagonal = height / Math.cos(slopeAngle);
  const depth = Math.sqrt(dormerDiagonal * dormerDiagonal - height * height) + getDormerFrontOffset(dormerSettings);
  dormerDiagonal = depth / Math.sin(slopeAngle);

  return Math.sqrt(dormerDiagonal * dormerDiagonal - depth * depth);
}

function getDormerRoofDefaultSlope(dormerSettings: DormerSettings): number {
  const roofType = getDormerRoofType(dormerSettings);
  let slope = 1.0;

  switch (roofType) {
    case DormerRoofType.SHED: {
      slope = 0.33;
      break;
    }
    case DormerRoofType.GABLE: {
      slope = 1.0;
      break;
    }
    default: {
      assertUnreachable(roofType);
    }
  }

  return slope;
}

function getDormerRoofType(dormerSettings: DormerSettings): DormerRoofType {
  return dormerSettings.roofType !== undefined ? dormerSettings.roofType : DormerRoofType.GABLE;
}

function createDormerRoofPerimeter(
  x: number,
  y: number,
  ux: number,
  uy: number,
  vx: number,
  vy: number,
  width: number,
  depth: number,
  baseRoofOverhang: number,
  wallConfig: WallSettings,
): PolyLine2 {
  const exteriorWallOffsetWithSheathing = getExteriorWallOffset(
    wallConfig,
    WallLateralDatumAnchor.OUTSIDE_FACE_OF_SUBSTRATE,
  );
  const perimeterWidth = width - 2.0 * exteriorWallOffsetWithSheathing;
  const perimeterDepth = depth + baseRoofOverhang;
  const perimeterStartX = x + ux * exteriorWallOffsetWithSheathing + vx * exteriorWallOffsetWithSheathing;
  const perimeterStartY = y + uy * exteriorWallOffsetWithSheathing + vy * exteriorWallOffsetWithSheathing;

  return polyLine2New([
    perimeterStartX,
    perimeterStartY,
    perimeterStartX + ux * perimeterWidth,
    perimeterStartY + uy * perimeterWidth,
    perimeterStartX + ux * perimeterWidth + vx * perimeterDepth,
    perimeterStartY + uy * perimeterWidth + vy * perimeterDepth,
    perimeterStartX + vx * perimeterDepth,
    perimeterStartY + vy * perimeterDepth,
    perimeterStartX,
    perimeterStartY,
  ]);
}

interface BuildDormerCornerBoardsOptions {
  outerEdge: PolyLine2;
  dormerSettings: DormerSettings;
  exteriorTrimSettings: ExteriorTrimSettings;
  profileSource: ActiveProfileSource;
  dormerBasePlane: Plane;
  dormerFrontHeight: number;
  slopeAngle: number;
  roofVersion: number;
  issues?: BuildingGenerationIssue[];
}

function buildDormerCornerBoards({
  outerEdge,
  dormerSettings,
  exteriorTrimSettings,
  profileSource,
  dormerBasePlane,
  dormerFrontHeight,
  slopeAngle,
  roofVersion,
  issues,
}: BuildDormerCornerBoardsOptions): FaceArrayViewEntity[] {
  const { enableOutsideCornerMoulding } = exteriorTrimSettings;

  const {
    cornerBoardProfile = exteriorTrimSettings.dormerCornerBoardProfile ?? ProfileExtrusionType.DEFAULT_PROFILE,
    cornerBoardMaterial = exteriorTrimSettings.dormerCornerBoardMaterial,
    cornerBoardBottomOffset = exteriorTrimSettings.dormerCornerBoardBottomOffset ?? 0,
    cornerBoardTopOffset = exteriorTrimSettings.dormerCornerBoardTopOffset ?? 0,
  } = dormerSettings;

  const faceArrays: FaceArrayViewEntity[] = [];
  const cornerBoardRails: CornerBoardRail[] = [];

  if (
    (!enableOutsideCornerMoulding && !dormerSettings.cornerBoardProfile && roofVersion > 2) ||
    cornerBoardProfile === ProfileExtrusionType.NONE
  ) {
    return faceArrays;
  }

  const descriptor = getActiveProfileDescriptor(
    profileSource,
    cornerBoardProfile,
    DEFAULT_DORMER_CORNER_TRIM_PROFILE_DESCRIPTOR,
  );
  if (!descriptor) {
    const exteriorTrimHandle: DocEntityHandle | undefined = exteriorTrimSettings.id
      ? { type: "SETTINGS", id: exteriorTrimSettings.id }
      : undefined;
    pushMissingProfileReferenceIssue(
      issues,
      profileSource,
      cornerBoardProfile,
      exteriorTrimHandle,
      "dormerCornerBoardProfile",
    );
    return faceArrays;
  }
  const profile = polyLine2Simplify(polyLine2Copy(getProfileExtrusionDescriptorProfile(descriptor)));
  const profileAABB = polyLine2AABB2(profile);
  const thickness = aabb2Width(profileAABB);
  const width = aabb2Height(profileAABB);
  const halfWidth = width * 0.5;

  const exteriorTrimViewNoteTable = mergeViewNoteTables(
    defaultAllExteriorTrimViewNotes,
    exteriorTrimSettings.viewNotes,
  );
  const dormerCornerBoardViewEntityDescriptor = viewEntityDescriptorNew(
    uid(),
    ViewEntityType.DORMER_CORNER,
    exteriorTrimViewNoteTable[ExteriorTrimViewNoteType.DORMER_CORNER],
  );

  polyLine2Move(profile, -profileAABB.maxX, -profileAABB.minY);

  const startZ = dormerBasePlane.originZ - Math.cos(slopeAngle) * halfWidth + cornerBoardBottomOffset;
  const extrusionPlane = planeNewXY(0, 0, startZ);
  const perimeter = planeReProjectPolyLine2(polyLine2Close(polyLine2Copy(outerEdge)), dormerBasePlane, extrusionPlane);
  const endZ = startZ + dormerFrontHeight - cornerBoardBottomOffset - cornerBoardTopOffset;

  let [ax, ay] = polyLine2EdgeStart(perimeter, 0);
  let [bx, by] = polyLine2EdgeStart(perimeter, 1);
  let [cx, cy] = polyLine2EdgeStart(perimeter, 2);

  const defaultAlignment = {
    floorIndex: -1,
    topOffset: 0,
    bottomOffset: 0,
    topAlignment: CornerBoardTrimAlignment.BO_SOFFIT,
    bottomAlignment: CornerBoardTrimAlignment.TO_FOUNDATION,
  };
  buildCornerBoardProfile({
    ax,
    ay,
    bx,
    by,
    cx,
    cy,
    wallOffsetEdge1: 0,
    wallOffsetEdge2: 0,
    hasBoardEdge1: true,
    hasBoardEdge2: true,
    profile,
    profileId: cornerBoardProfile,
    material: cornerBoardMaterial,
    width,
    thickness,
    cornerCondition: ValidCornerCondition.OUTSIDE,
    startZ,
    endZ,
    faceArrays,
    cornerBoardRails,
    viewEntityDescriptor: dormerCornerBoardViewEntityDescriptor,
    ...defaultAlignment,
  });

  [ax, ay] = polyLine2EdgeStart(perimeter, 1);
  [bx, by] = polyLine2EdgeStart(perimeter, 2);
  [cx, cy] = polyLine2EdgeStart(perimeter, 3);

  buildCornerBoardProfile({
    ax,
    ay,
    bx,
    by,
    cx,
    cy,
    wallOffsetEdge1: 0,
    wallOffsetEdge2: 0,
    hasBoardEdge1: true,
    hasBoardEdge2: true,
    profile,
    profileId: cornerBoardProfile,
    material: cornerBoardMaterial,
    width,
    thickness,
    cornerCondition: ValidCornerCondition.OUTSIDE,
    startZ,
    endZ,
    faceArrays,
    cornerBoardRails,
    viewEntityDescriptor: dormerCornerBoardViewEntityDescriptor,
    ...defaultAlignment,
  });

  return faceArrays;
}

interface RoofDormerOnFaceOptions extends MergeSettingsForEntityParams {
  roofIndex: number;
  roofSystemId: string;
  level: Level;
  clipContext: MultiGableRoofClipContext;
  subRoof: GeneratedRoof;
  side: RoofSide;
  face: Face;
  dormerInputSettings: DormerInputSettings;
  roofParentSettings: DormerParentRoofSettings;
  dormer: RoofDormerPlacement;
  windowCatalog: Table<WindowDescriptor>;
  windows: RWTable<GeneratedWindowPlacement>;
  windowUnits: RWTable<WindowUnit>;
  windowShutters: RWTable<WindowShutters>;
  issues: BuildingGenerationIssue[];
  u?: number;
  v?: number;
  addWindowShutters?: boolean;
  addRoofWallFaces: boolean;
  generatedDormerRoofVolumes: RWTable<GeneratedRoofVolumeLegacy>;
  generatedDormerRoofs: RWTable<GeneratedRoof>;
  mirror: boolean;
  entityHandleForSelectingReportingUnit: DocEntityHandle | undefined;
}

export async function roofDormerOnFace({
  roofIndex,
  roofSystemId,
  level,
  clipContext,
  subRoof,
  side,
  face,
  dormerInputSettings,
  roofParentSettings,
  dormer,
  windowCatalog,
  windows,
  windowUnits,
  windowShutters,
  issues,
  u = 100,
  v = 20,
  // see TODO below ; addWindowShutters = false,
  generatedDormerRoofVolumes,
  generatedDormerRoofs,
  mirror,
  addWindowShutters,
  addRoofWallFaces,
  entitySettings,
  activeSettingsSets,
  optionChoices,
  settings,
  entityHandleForSelectingReportingUnit,
}: RoofDormerOnFaceOptions): Promise<RoofDormer | null> {
  if (!subRoof) {
    return null;
  }

  const {
    roofFramingSettings,
    roofPlanSettings,
    dormerSettings,
    wallSettings,
    exteriorTrimSettings,
    propCatalog,
    assets,
    wainscotingSettings,
    profileSource,
    roofElevationPlanSettings,
    roofSettings: { overhangDatum = WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING },
    getMergedSettingsForWindow,
    getMergedWindowSettingsForDormer,
  } = dormerInputSettings;

  const hideInteriorFaces: Face[] = [];
  const dormerWindows: RWTable<GeneratedWindowPlacement> = {};
  const cornerBoards: FaceArrayViewEntity[] = [];

  const mergedDormerSettings = getMergedDormerSettings({
    dormer,
    dormerSettings,
    entitySettings,
    activeSettingsSets,
    optionChoices,
    settings,
  });

  const dormerFrontWidth = getDormerWidth(mergedDormerSettings, dormer, windowCatalog);

  const { roofVolume: mainRoofVolume, name: mainRoofName, roofVersion } = subRoof;

  const {
    heelHeightOffsets: mainRoofHeelHeightOffsets,
    leftSideSlopes: mainRoofLeftSlopes,
    rightSideSlopes: mainRoofRightSlopes,
    verticalRails: mainRoofVerticalRails,
    baseHeight: mainRoofBaseHeight,
  } = mainRoofVolume;

  const { useNewPlacementMethod } = dormer;

  const wallToPlanarGraphEdgeDistance = getExteriorWallOffset(wallSettings, overhangDatum);
  let wallToRoofEdgeDistance;
  if (roofVersion > 3) {
    const roofEdgeFromSide = getRoofEdgeFromSide(mainRoofVolume, side);
    assertIsNotNullOrUndefined(roofEdgeFromSide);
    wallToRoofEdgeDistance = roofEdgeFromSide.sideOverhang - wallToPlanarGraphEdgeDistance;
  } else {
    const roofEdgeFromSide = getRoofEdgeFromSideOld(subRoof, side);
    assertIsNotNullOrUndefined(roofEdgeFromSide);
    wallToRoofEdgeDistance = roofEdgeFromSide.sideOverhang - wallToPlanarGraphEdgeDistance;
  }

  const finalU = u - dormerFrontWidth / 2 + wallToRoofEdgeDistance;
  const finalV = useNewPlacementMethod ? v : v - getDormerFrontOffset(mergedDormerSettings) + wallToRoofEdgeDistance;

  const [baseX, baseY, z] = planeEval(face.plane, finalU, finalV);
  let x = baseX;
  let y = baseY;
  if (useNewPlacementMethod) {
    const offset = -getDormerFrontOffset(mergedDormerSettings) + wallToRoofEdgeDistance;
    const [dirX, dirY] = vec2Normalize(-face.plane.uY, face.plane.uX);
    x += dirX * offset;
    y += dirY * offset;
  }

  const [refX, refY, refZ] = planeEval(face.plane, finalU, 0);

  // project 0, 0, 1 onto the plane to get the direction of the dormer

  const [uu, uv] = planeProject(face.plane, face.plane.originX, face.plane.originY, face.plane.originZ + 1);

  let [slopex, slopey, slopez] = planeEvalDir(face.plane, uu, uv);
  [slopex, slopey, slopez] = vec3Normalize(slopex, slopey, slopez);

  const [ux, uy, uz] = vec3CrossNormalize(slopex, slopey, slopez, 0, 0, 1);
  const [vx, vy, vz] = vec3CrossNormalize(0, 0, 1, ux, uy, uz);

  const slopeAngle = vec3AngleBetween(0, 0, 1, slopex, slopey, slopez);

  const dormerFrontHeight = getDormerHeight(mergedDormerSettings, dormer, slopeAngle, windowCatalog);
  const baseHeight = z + dormerFrontHeight;

  const dormerDiagonal = dormerFrontHeight / Math.cos(slopeAngle);

  const dormerDepth =
    Math.sqrt(dormerDiagonal * dormerDiagonal - dormerFrontHeight * dormerFrontHeight) +
    getDormerFrontOffset(mergedDormerSettings);
  const outerEdge =
    dormerDepth !== 0
      ? polyLine2New([dormerFrontWidth, dormerDepth, dormerFrontWidth, 0, 0, 0, 0, dormerDepth])
      : polyLine2ByTwoPoints(dormerFrontWidth, 0, 0, 0);

  const wallThickness = wallSettings.exteriorWallBaseThickness;
  const innerEdge = offsetPolyLine2(outerEdge, wallThickness);
  const wallSlab = polyLine2Concat(outerEdge, polyLine2Reverse(innerEdge), true);

  const dormerBasePlane = planeNew(x, y, z, ux, uy, uz, vx, vy, vz);
  const wallBRep =
    dormerFrontHeight > DIST_EPSILON
      ? extrusionToBRep(extrudePolyLine2(wallSlab, dormerBasePlane, 0, 0, dormerFrontHeight))
      : brepNew([], []);

  const bottomWallZ = subRoof.roofVolume.baseHeight - getEaveDepth(subRoof.roofSettings);
  const dormerBottomWallBasePlane = planeNew(x, y, bottomWallZ, ux, uy, uz, vx, vy, vz);
  const bottomWallHeight = z - bottomWallZ;
  const bottomWallBRep =
    useNewPlacementMethod && dormerFrontHeight > DIST_EPSILON && bottomWallHeight > DIST_EPSILON && y < DIST_EPSILON
      ? extrusionToBRep(extrudePolyLine2(wallSlab, dormerBottomWallBasePlane, 0, 0, bottomWallHeight))
      : brepNew([], []);

  const windowHoleFaces: Face[] = [];

  const dormerHandle: DormerHandle = {
    type: "DORMER",
    id: dormer.id,
  };
  const fallbackInstanceId = subRoof.componentInstanceChain?.[0];

  const { useLegacyAttachmentSettings } = mergedDormerSettings ?? { useLegacyAttachmentSettings: true };

  if (dormerFrontHeight > DIST_EPSILON && wallBRep.faces.length > 0) {
    const frontFaceIndex = 3;
    const backFaceIndex = 7;

    const frontFace = wallBRep.faces[frontFaceIndex];
    const backFace = wallBRep.faces[backFaceIndex];
    cornerBoards.push(
      ...buildDormerCornerBoards({
        outerEdge,
        exteriorTrimSettings,
        dormerSettings: mergedDormerSettings,
        profileSource,
        dormerBasePlane,
        dormerFrontHeight,
        slopeAngle,
        roofVersion,
        issues,
      }),
    );

    let streetOrientation = StreetOrientation.FRONT;

    if (!useLegacyAttachmentSettings) {
      if (mergedDormerSettings?.side) {
        if (mergedDormerSettings?.side === BuildingSide.LEFT) {
          streetOrientation = StreetOrientation.LEFT;
        } else if (mergedDormerSettings?.side === BuildingSide.RIGHT) {
          streetOrientation = StreetOrientation.RIGHT;
        } else if (mergedDormerSettings?.side === BuildingSide.BACK) {
          streetOrientation = StreetOrientation.BACK;
        }
      }
    }

    const windowsToInsert = getDormerWindows(
      dormer,
      getMergedWindowSettingsForDormer,
      streetOrientation,
      windowCatalog,
    );

    if (windowsToInsert.length > 0) {
      // generate the temporal face before adding any holes to the front face
      const hideInteriorFace = faceOrNullCopy(wallBRep.faces[frontFaceIndex]);
      if (hideInteriorFace) {
        faceOrNullMoveAlongNormal(hideInteriorFace, -wallThickness);
        hideInteriorFaces.push(hideInteriorFace);
      }
    }

    for (let i = 0, l = windowsToInsert.length; i < l; i++) {
      const window = windowsToInsert[i];
      const holeData = windowRectangularOpening(window, undefined, dormerFrontWidth, undefined);

      if (!holeData) {
        issues.push({
          type: BuildingGenerationIssueType.WINDOW_OPENING_EXCEEDS_BOUNDARY,
          handle: dormerHandle,
          params: [
            {
              type: "RANGE",
              description: "window width",
              value: window.descriptor.width,
              max: dormerFrontWidth,
              issueType: "MAX_EXCEEDED",
            },
          ],
        });
        continue;
      }

      const holeOutline = holeData.opening2;

      if (
        !holeWithBackFaceIndex(
          wallBRep,
          windowHoleFaces,
          frontFaceIndex,
          backFaceIndex,
          polyLine2Reverse(polyLine2Copy(holeOutline)),
        )
      ) {
        issues.push({
          type: BuildingGenerationIssueType.WINDOW_OPENING_EXCEEDS_BOUNDARY,
          handle: dormerHandle,
        });
        continue;
      }

      const mergedWindowSettings = getMergedSettingsForWindow(window, window.descriptor);

      const { hasExteriorCasing } = window;

      window.opening = windowOpeningNew(
        window,
        mergedWindowSettings,
        {
          edge: outerEdge,
          edgeLength: polyLine2EdgeLength(outerEdge, 0),
          edgePlane: frontFace.plane,
          openingPlane: backFace.plane,
          room: undefined,
          roomKind: undefined,
        },
        {
          ...makeOpeningSide({
            opening2: holeOutline,
            openingRect2: holeOutline,
            context: {
              condition: "INTERIOR",
              openingPlane: frontFace.plane,
            },
          }),
          opening3: polyLine3OnPlane(holeOutline, backFace.plane),
        },
        // @todo(Andrew): what should the back placement of a dormer be based on?
        {
          edge: innerEdge,
          edgeLength: polyLine2EdgeLength(innerEdge, 0),
          edgePlane: frontFace.plane,
          openingPlane: frontFace.plane,
          room: undefined,
          roomKind: undefined,
        },
        {
          ...makeOpeningSide({
            opening2: frontFace.loops[1],
            openingRect2: frontFace.loops[1],
            hasCasing: hasExteriorCasing,
            context: {
              condition: "EXTERIOR",
              openingPlane: backFace.plane,
            },
          }),
          opening3: polyLine3OnPlane(frontFace.loops[1], frontFace.plane),
        },
        mirror,
      );

      if (window.handle) {
        BuildingGenerationContext.indexAddNode(window.handle, window.opening?.aabb3 ?? undefined);
      }

      const windowId = window.id;

      dormerWindows[windowId] = window;
      windows[windowId] = window;

      const windowUnit = await makeWindowUnit({
        window,
        issues,
        wallLength: dormerFrontWidth,
        wallThickness,
        wallSettings,
        propCatalog,
        assets,
        wainscotingSettings,
        roomOutlines: undefined,
        wallMassSystem: undefined,
        datumAlignmentOffset: wallThickness,
        mergedWindowSettings,
        context: {
          isLeft: true,
          isFront: true,
        },
        streetOrientation,
        useLegacyAttachmentSettings,
        entityHandleForSelectingReportingUnit,
        fallbackInstanceId,
      });

      if (windowUnit) {
        windowUnits[windowId] = windowUnit;

        let shuttersPlacement: WindowShuttersPlacement | null = null;

        if (useLegacyAttachmentSettings) {
          shuttersPlacement = makeDefaultWindowShuttersPlacementLegacy(mergedWindowSettings.shutters, window);
          if (
            window.descriptor.width + 2 * shuttersPlacement.size > dormerFrontWidth ||
            shuttersPlacement.size < DIST_EPSILON ||
            !addWindowShutters
          ) {
            shuttersPlacement = null;
          }
        } else if (addWindowShutters) {
          shuttersPlacement = makeDefaultWindowShuttersPlacement(
            mergedWindowSettings.shutters,
            window,
            dormerFrontWidth,
            mirror,
          );
        }

        if (shuttersPlacement) {
          const newShutters = makeWindowShutters({
            handle: dormerHandle,
            window,
            shuttersPlacement,
            roomOutlines: undefined,
            wallMassSystem: undefined,
            issues,
            minZ: Number.NEGATIVE_INFINITY,
            fallbackInstanceId,
          });
          if (newShutters) {
            windowShutters[windowId] = newShutters;
          }
        }
      }
    }
  }

  const { truncatedStartSlopes: mainRoofStartSlopes, truncatedEndSlopes: mainRoofEndSlopes } = mainRoofVerticalRails;

  let mainRoofSlopes: RoofSlope[] = [];
  let mainRoofOppSlopes: RoofSlope[] = [];
  let mainRoofBaseHeightOffset = mainRoofHeelHeightOffsets.refOffset;

  switch (side) {
    case RoofSide.LEFT:
      mainRoofSlopes = mainRoofLeftSlopes;
      mainRoofOppSlopes = mainRoofRightSlopes;
      mainRoofBaseHeightOffset = mainRoofHeelHeightOffsets.leftOffset;
      break;
    case RoofSide.RIGHT:
      mainRoofSlopes = mainRoofRightSlopes;
      mainRoofOppSlopes = mainRoofLeftSlopes;
      mainRoofBaseHeightOffset = mainRoofHeelHeightOffsets.rightOffset;
      break;
    case RoofSide.START:
      mainRoofSlopes = mainRoofStartSlopes;
      mainRoofOppSlopes = mainRoofEndSlopes;
      break;
    case RoofSide.END:
      mainRoofSlopes = mainRoofEndSlopes;
      mainRoofOppSlopes = mainRoofStartSlopes;
      break;
    case RoofSide.INVALID:
      break;
  }

  const dormerRoofType = getDormerRoofType(mergedDormerSettings);
  const isShedRoof = dormerRoofType === DormerRoofType.SHED;

  const exteriorWallOffsetFromOverhangDatum = getExteriorWallOffset(wallSettings, overhangDatum);
  const baseRoofOverhang = mergedDormerSettings.overhang ?? (isShedRoof ? 8 : 6);
  const roofOverhang = baseRoofOverhang + exteriorWallOffsetFromOverhangDatum;
  const { vX, vY, vZ } = face.plane;
  const relativeDistSign = vec3Dot(x - refX, y - refY, z - refZ, vX, vY, vZ) < 0 ? -1 : 1;
  const relativeDist = relativeDistSign * vec2Dist(x, y, refX, refY) + dormerDepth;

  const emptyNeighborRoof = neighborRoofNew(baseHeight);
  const hostNeighborRoof = neighborRoofNew(
    mainRoofBaseHeight,
    mainRoofBaseHeightOffset,
    roofOverhang,
    roofOverhang,
    roofOverhang,
    relativeDist,
    0,
    mainRoofSlopes,
    mainRoofOppSlopes,
    subRoof.id,
    side,
  );

  const leftNeighborRoof = isShedRoof ? hostNeighborRoof : emptyNeighborRoof;
  const rightNeighborRoof = emptyNeighborRoof;
  const startNeighborRoof = emptyNeighborRoof;
  const endNeighborRoof = isShedRoof ? emptyNeighborRoof : hostNeighborRoof;

  const subRoofPerimeter = createDormerRoofPerimeter(
    x,
    y,
    ux,
    uy,
    vx,
    vy,
    dormerFrontWidth,
    dormerDepth,
    baseRoofOverhang,
    wallSettings,
  );

  const startEdge = isShedRoof ? 3 : 0;
  const endEdge = (startEdge + 2) % 4;
  const polygonId = -1;
  const dormerRoofId = uid();
  const dormerRoofName = mainRoofName + " Dormer";

  const slopes = getSubRoofSlopes({
    exactPerimeter: subRoofPerimeter,
    boundingPerimeter: subRoofPerimeter,
    startEdge,
    roofSettings: mergedDormerSettings,
    defaultSlope: getDormerRoofDefaultSlope(mergedDormerSettings),
  });

  const roofOpenings: RoofOpeningPlacement[] = [];
  if (dormer.gableOpeningPlacement) {
    roofOpenings.push({ ...dormer.gableOpeningPlacement, useLegacyAttachmentSettings });
  }

  const finalDormerRoofSettings = getFinalDormerRoofSettings(subRoof.roofSettings, mergedDormerSettings);

  const finalRoofSettings = {
    ...finalDormerRoofSettings,
    slopes,
    rightSlopes: slopes,
    startTruncationType: RoofTruncationType.NONE,
    endTruncationType: RoofTruncationType.NONE,
    rakeOverhang: baseRoofOverhang,
    eaveOverhang: baseRoofOverhang,
    overhangRearOffset: 0,
    overhangSideOffset: 0,
    roofType: isShedRoof ? GableRoofType.RIGHT_SIDE_SHED_ROOF : GableRoofType.DEFAULT,
  } as RemoveFormula<RoofSettings>;

  const finalPlanSettings = getFinalRoofPlanSettings(dormer.dormerPlanSettings, roofPlanSettings);
  const finalRoofFramingSettings = getFinalDormerFramingSettings(dormer, roofFramingSettings);
  const finalDormerElevationPlanSettings = {
    ...roofElevationPlanSettings,
    ...roofParentSettings.roofElevationPlanSettings,
    ...dormer.dormerElevationPlanSettings,
  };

  const finalRoofVolumeInputSettings: RoofVolumeInputSettings = {
    ...dormerInputSettings,
    dormerSettings: mergedDormerSettings,
    roofElevationPlanSettings: finalDormerElevationPlanSettings,
    roofPlanSettings: finalPlanSettings,
    roofSettings: finalRoofSettings,
    roofFramingSettings: finalRoofFramingSettings,
  };

  const finalRoofInputSettings: RoofInputSettings = {
    ...dormerInputSettings,
    dormerSettings: mergedDormerSettings,
    roofElevationPlanSettings: finalDormerElevationPlanSettings,
    roofPlanSettings: finalPlanSettings,
    roofSettings: finalRoofSettings,
    roofFramingSettings: finalRoofFramingSettings,
  };

  const { wallMaterial } = mergedDormerSettings;

  const insertedRoofVolumes: Table<GeneratedRoofVolumeLegacy> = {
    [subRoof.id]: mainRoofVolume,
  };

  const insertedRoofs: Table<GeneratedRoof> = {
    [subRoof.id]: subRoof,
  };

  const neighborRoofs = neighborRoofsNew(startNeighborRoof, endNeighborRoof, leftNeighborRoof, rightNeighborRoof);

  const startSideOverrides: EvaluatedRoofEdgeOverrides = {
    truncationType: mergedDormerSettings.truncationType,
    heelHeightOffset: mergedDormerSettings.truncationHeightFactor,
    normalizedHeelHeightOffset: mergedDormerSettings.truncationNormalizedHeight,
    truncationSlopeType: mergedDormerSettings.truncationSlopeType,
    slope: mergedDormerSettings.truncationSlope ?? slopes[0].slope,
  };

  const endSideOverrides: EvaluatedRoofEdgeOverrides = isShedRoof ? { ...startSideOverrides } : {};

  const roofSideOverrides: EvaluatedRoofSideOverridesTable = {
    [RoofSide.LEFT]: {},
    [RoofSide.RIGHT]: {},
    [RoofSide.START]: startSideOverrides,
    [RoofSide.END]: endSideOverrides,
  };

  const roofData: EvaluatedRoofData = {
    parentId: roofSystemId,
    roofVersion,
    blocks: [],
    roofSideOverrides,
    roofEdgeOverrides: defaultRoofEdgeOverrides,
  };

  const inputPerimeters = roofInputPerimetersNew(
    polyLine2Copy(subRoofPerimeter),
    polyLine2Copy(subRoofPerimeter),
    polyLine2Copy(subRoofPerimeter),
    polyLine2Copy(subRoofPerimeter),
    polyLine2Copy(subRoofPerimeter),
  );

  const dormerRoofVolumeOpts: GenerateRoofVolumeOptions = {
    roofData,
    roofId: dormerRoofId,
    index: roofIndex,
    name: subRoof.name,
    level,
    roofInputSettings: finalRoofVolumeInputSettings,
    polygonId,
    inputPerimeters,
    startEdge,
    peakDirection: RoofPeakDirection.VERTICAL,
    endEdge,
    baseHeight,
    topOfWallHeight: baseHeight - getEaveDepth(finalRoofSettings),
    neighborRoofs,
    insertedRoofVolumes,
    clipContext,
    issues,
    mirror,
    isDormer: true,
  };

  const { roofVolume } = generateRoofVolumeLegacy(dormerRoofVolumeOpts);

  const subGableOpts: GenerateRoofOptions = {
    roofVolume,
    roofData,
    insertedRoofs,
    roofId: dormerRoofId,
    name: dormerRoofName,
    roofInputSettings: finalRoofInputSettings,
    inputPerimeters,
    roofOpenings,
    topOfWallHeight: baseHeight - getEaveDepth(finalRoofSettings),
    wallMaterial,
    issues,
    windowCatalog,
    windows,
    windowUnits,
    windowShutters,
    addRoofWallFaces,
  };

  let dormerSubRoof = await generateMultiGableRoof(subGableOpts);
  // The dormer's sub-roof is selected as the dormer.
  dormerSubRoof = { ...dormerSubRoof, pickHandle: dormerHandle };

  // Filter out the top and bottom faces.  Because the top faces + geometry generated from
  // design materials will poke through the top of the roof
  const allWallFaces = [...wallBRep.faces, ...bottomWallBRep.faces];
  const facesWithoutTopBottom = allWallFaces.filter((f) => {
    const [nx, ny, nz] = planeNormal(f.plane);
    const d = Math.abs(vec3Dot(nx, ny, nz, 0, 0, 1));
    return d < 1.0 - DIST_EPSILON;
  });

  const frontEdge = polyLine2ByTwoPoints(0, 0, dormerFrontWidth, 0);
  const dormerBaseToPeak = dormerSubRoof.roofVolume.peakHeight - baseHeight;

  let wallBaseBoardRails: RoofDormerWallBaseBoardRails | undefined = undefined;
  if (dormerFrontHeight > 0) {
    const perimeter = planeReProjectPolyLine2(outerEdge, dormerBasePlane, planeNewXY());
    if (isShedRoof && polyLine2EdgeCount(perimeter) >= 3) {
      const { rails } = dormerSubRoof;
      const { peakRail } = rails;
      const [x, y] = polyLine3EdgeStart(peakRail, 0);
      const [tx, ty] = polyLine3EdgeTangent(peakRail, 0);

      const plen = perimeter.length;

      [perimeter[0], perimeter[1]] = ray2Project(x, y, tx, ty, perimeter[0], perimeter[1]);
      [perimeter[plen - 2], perimeter[plen - 1]] = ray2Project(x, y, tx, ty, perimeter[plen - 2], perimeter[plen - 1]);
    }

    wallBaseBoardRails = buildDormerWallBaseBoardRails({
      dormerSettings: mergedDormerSettings,
      perimeter,
      clipContext,
      maxHeight: isShedRoof ? dormerSubRoof.roofVolume.peakHeight : baseHeight,
    });
  }

  generatedDormerRoofVolumes[dormerSubRoof.id] = roofVolume;
  generatedDormerRoofs[dormerSubRoof.id] = dormerSubRoof;

  return roofDormerNew(
    dormerHandle,
    entityHandleForSelectingReportingUnit,
    roofSystemId,
    subRoof.id,
    dormerSubRoof.id,
    dormerSubRoof.levelId,
    polyLine2OnPlane(dormerBasePlane, frontEdge),
    dormerFrontHeight,
    dormerFrontHeight + dormerBaseToPeak,
    facesWithoutTopBottom,
    hideInteriorFaces,
    windowHoleFaces,
    dormerWindows,
    cornerBoards,
    wallBaseBoardRails,
    wallMaterial,
  );
}

interface BuildDormerWallBaseBoardRailsOptions {
  dormerSettings: DormerSettings;
  perimeter: PolyLine2;
  clipContext: MultiGableRoofClipContext;
  maxHeight: number;
}

function buildDormerWallBaseBoardRails({
  dormerSettings,
  perimeter,
  clipContext,
  maxHeight,
}: BuildDormerWallBaseBoardRailsOptions): RoofDormerWallBaseBoardRails | undefined {
  const { wallBaseBoardMode = DormerWallBaseBoardMode.NONE, wallBaseBoard = ProfileExtrusionType.DEFAULT_PROFILE } =
    dormerSettings;

  if (wallBaseBoardMode === DormerWallBaseBoardMode.NONE || polyLine2EdgeCount(perimeter) < 3) {
    return undefined;
  }

  const { discardNoDormersBSP } = clipContext;

  const sections: RoofDormerWallBaseBoardSection[] = [];

  const addPerimeterSegment = (
    segment: PolyLine2,
    clipPlane: Plane | null = null,
    startCapPlane: Plane | null = null,
    endCapPlane: Plane | null = null,
  ) => {
    const segments = clipRail({
      rail: segment,
      height: maxHeight,
      options: {
        clippingNodes: [clippingNodeNew(discardNoDormersBSP)],
        epsilon: MULTIGABLE_BSP_EPSILON,
        coplanarsToFront: true,
      },
    });

    sections.push(roofDormerWallBaseBoardSectionNew(segments, clipPlane, startCapPlane, endCapPlane));
  };

  if (
    wallBaseBoardMode === DormerWallBaseBoardMode.FRONT_AND_SIDES ||
    wallBaseBoardMode === DormerWallBaseBoardMode.SIDES
  ) {
    const [x, y] = polyLine2EdgeStart(perimeter, 1);
    const [tx, ty] = polyLine2EdgeTangent(perimeter, 1);

    const clipPlane = planeNew(x, y, 0, -tx, -ty, 0, 0, 0, 1);
    const capPlane = planeNew(0, 0, 0, -tx, -ty, 0, 0, 0, 1);

    addPerimeterSegment(polyLine2Edge(perimeter, 0), clipPlane, null, capPlane);
    addPerimeterSegment(polyLine2Edge(perimeter, 2), clipPlane, capPlane, null);
  }

  if (
    wallBaseBoardMode === DormerWallBaseBoardMode.FRONT_AND_SIDES ||
    wallBaseBoardMode === DormerWallBaseBoardMode.FRONT
  ) {
    addPerimeterSegment(polyLine2Edge(perimeter, 1));
  }

  return roofDormerWallBaseBoardRailsNew(sections, maxHeight, wallBaseBoard);
}

/**
 * @deprecated This method was buggy and has been replaced by getRoofEdgeFromSide,
 * but it is still used for old dormers as they were relying on the buggy behavior.
 */
function getRoofEdgeFromSideOld(subRoof: GeneratedRoof, side: RoofSide): RoofEdge | null {
  const {
    roofVolume: { edges },
  } = subRoof;

  switch (side) {
    case RoofSide.START:
      return edges[0];
    case RoofSide.RIGHT:
      return edges[1];
    case RoofSide.END:
      return edges[2];
    case RoofSide.LEFT:
      return edges[3];
    default:
      return null;
  }
}
